import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Shield, Clock, Users, Award, Star, CheckCircle, Heart, Target, Globe, MapPin, Phone, Mail, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import tropuncaLogo from "@/assets/tropunca-logo.png";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import GoldenParticles from "@/components/GoldenParticles";
import WaveGoldDivider from "@/components/WaveGoldDivider";
import VIPBadge from "@/components/VIPBadge";
import AnimatedSection from "@/components/AnimatedSection";
import AnimatedCounter from "@/components/AnimatedCounter";

const About = () => {
  const { getConfig } = useSiteConfig();
  
  const stats = [
    { icon: Star, number: 10, suffix: "+", label: "Years of Excellence", description: "Serving VIP travelers" },
    { icon: Users, number: 5000, suffix: "+", label: "Happy VIP Guests", description: "Satisfied clients worldwide" },
    { icon: Award, number: 4.9, suffix: "/5", label: "Average Rating", description: "Based on verified reviews" },
    { icon: CheckCircle, number: 24, suffix: "/7", label: "VIP Support", description: "Always here for you" }
  ];

  const values = [
    { icon: Shield, title: "Safety First", description: "Your safety is our top priority. All our vehicles are regularly inspected and maintained, and our drivers are fully licensed and insured." },
    { icon: Heart, title: "VIP Excellence", description: "We're dedicated to providing exceptional VIP service that exceeds your expectations. Your satisfaction is our success." },
    { icon: Target, title: "Reliability", description: "Count on us for punctual service, transparent pricing, and consistent quality. We're committed to making your journey stress-free." },
    { icon: Globe, title: "Local Expertise", description: "As locals, we know Punta Cana and the Dominican Republic inside out. Let us share our knowledge and passion with you." }
  ];

  const team = [
    { name: "Professional VIP Drivers", role: "Bilingual & Licensed", description: "Our drivers are carefully selected, professionally trained, and fluent in English and Spanish." },
    { name: "VIP Concierge Team", role: "24/7 Available", description: "Our dedicated support team is always ready to assist you before, during, and after your journey." },
    { name: "Expert Tour Guides", role: "Local Specialists", description: "Passionate about sharing the beauty and culture of the Dominican Republic with our VIP guests." }
  ];

  const whyChooseUs = [
    "Licensed and fully insured VIP operations",
    "24/7 bilingual VIP concierge support",
    "Professional and experienced drivers",
    "Luxury, clean, and comfortable fleet",
    "Transparent pricing with no hidden fees",
    "Instant booking confirmation",
    "Flexible cancellation policies",
    "Real-time flight tracking for airport pickups"
  ];

  return (
    <Layout>
      <Helmet>
        <title>About Us | TROPUNCA VIP - Your Premier VIP Transfer & Tour Service</title>
        <meta name="description" content="Learn about TROPUNCA VIP - your trusted partner for luxury airport transfers and exclusive tours in Punta Cana and throughout the Dominican Republic." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-32 bg-vip-dark overflow-hidden">
        <GoldenParticles count={25} />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <div className="mb-8 flex justify-center">
              <img src={tropuncaLogo} alt="TROPUNCA VIP" className="h-28 w-auto animate-float-slow" />
            </div>
            <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
              Your Premier VIP Service
            </VIPBadge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white">
              About <span className="text-gradient-gold">TROPUNCA VIP</span>
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Experience the Caribbean with comfort, reliability, and VIP style. We're your trusted partner for seamless luxury travel in Punta Cana and throughout the Dominican Republic.
            </p>
          </AnimatedSection>
        </div>
        
        <WaveGoldDivider className="absolute bottom-0 left-0 right-0" dark />
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="text-center border-primary/20 hover:border-primary/50 transition-all hover:shadow-gold hover:-translate-y-2">
                    <CardContent className="pt-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-gold mb-4">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-primary mb-2">
                        <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                      </div>
                      <div className="text-lg font-semibold mb-1">{stat.label}</div>
                      <div className="text-sm text-muted-foreground">{stat.description}</div>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slide-left">
              <VIPBadge variant="gold" className="mb-4">Our Story</VIPBadge>
              <h2 className="text-4xl font-bold mb-6 text-gradient-gold">
                Connecting Travelers to Caribbean Paradise
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  TROPUNCA VIP was born from a passion for hospitality and a deep love for the Dominican Republic. Founded over a decade ago, we started with a simple mission: to provide VIP travelers with safe, comfortable, and reliable luxury transportation in Punta Cana.
                </p>
                <p>
                  What began as a small transfer service has grown into a comprehensive luxury travel solution, serving thousands of satisfied VIP guests from around the world. Our commitment to excellence and personalized service has made us a trusted name in Dominican tourism.
                </p>
                <p>
                  Today, we're proud to offer not just airport transfers, but also unforgettable exclusive tours that showcase the natural beauty, rich culture, and warm hospitality that make the Dominican Republic a world-class destination.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="slide-right" className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-gold">
                <img 
                  src="https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80" 
                  alt="Punta Cana Beach"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-gold text-white p-6 rounded-xl shadow-gold max-w-xs">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-6 h-6" />
                  <span className="font-bold text-lg">Based in Punta Cana</span>
                </div>
                <p className="text-sm opacity-90">Serving all of Dominican Republic</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={15} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <AnimatedSection animation="slide-left">
              <Card className="border-2 border-primary/30 bg-white/5 backdrop-blur-sm hover:border-primary/60 transition-all hover:shadow-gold">
                <CardHeader>
                  <div className="w-14 h-14 rounded-full bg-gradient-gold flex items-center justify-center mb-4">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">Our Mission</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80 leading-relaxed">
                    To provide safe, reliable, and comfortable VIP transportation and tour services that exceed our guests' expectations, while showcasing the beauty and culture of the Dominican Republic with professionalism and care.
                  </p>
                </CardContent>
              </Card>
            </AnimatedSection>

            <AnimatedSection animation="slide-right">
              <Card className="border-2 border-primary/30 bg-white/5 backdrop-blur-sm hover:border-primary/60 transition-all hover:shadow-gold">
                <CardHeader>
                  <div className="w-14 h-14 rounded-full bg-gradient-gold flex items-center justify-center mb-4">
                    <Globe className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">Our Vision</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80 leading-relaxed">
                    To be the most trusted and preferred VIP transfer and tour service in the Dominican Republic, known for exceptional customer service, reliability, and creating unforgettable luxury travel experiences.
                  </p>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Our Values</VIPBadge>
            <h2 className="text-4xl font-bold mb-4 text-gradient-gold">What Drives Us</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These core values guide everything we do, from the moment you book to the end of your VIP journey.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="border-2 border-transparent hover:border-primary/30 transition-all hover:shadow-gold group hover:-translate-y-2">
                    <CardHeader>
                      <div className="w-16 h-16 rounded-2xl bg-gradient-gold flex items-center justify-center mb-4 group-hover:animate-pulse-glow transition-all">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <CardTitle className="text-xl">{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Our Team</VIPBadge>
            <h2 className="text-4xl font-bold mb-4 text-gradient-gold">Meet the People Behind Your VIP Journey</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our dedicated team is committed to making your Dominican Republic experience exceptional.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <AnimatedSection key={index} delay={index * 150}>
                <Card className="text-center border-primary/20 hover:border-primary/50 transition-all hover:shadow-gold hover:-translate-y-2">
                  <CardHeader>
                    <div className="w-20 h-20 rounded-full bg-gradient-gold mx-auto mb-4 flex items-center justify-center">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                    <CardTitle className="text-xl">{member.name}</CardTitle>
                    <CardDescription className="text-primary font-semibold">{member.role}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <VIPBadge variant="gold" className="mb-4">Why Choose Us</VIPBadge>
              <h2 className="text-4xl font-bold mb-4 text-gradient-gold">
                The TROPUNCA VIP Difference
              </h2>
              <p className="text-muted-foreground">
                Here's what sets us apart from other transfer and tour services
              </p>
            </AnimatedSection>

            <AnimatedSection>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-lg hover:bg-primary/5 transition-colors group">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                    <span className="text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vip-dark text-white relative overflow-hidden">
        <GoldenParticles count={20} />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <Crown className="h-16 w-16 mx-auto mb-6 text-primary animate-float" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Experience <span className="text-gradient-gold">TROPUNCA VIP</span> Service?
            </h2>
            <p className="text-xl mb-8 text-white/80">
              Book your transfer or tour today and discover why thousands of VIP travelers trust us
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-8">
                <Link to="/transfers">Book a VIP Transfer</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary/50 text-white hover:bg-primary/20 text-lg px-8">
                <Link to="/tours">Explore Exclusive Tours</Link>
              </Button>
            </div>

            <div className="mt-12 pt-12 border-t border-primary/30">
              <p className="text-lg mb-6 font-semibold text-primary">Get in Touch</p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <a href="tel:+18091234567" className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Phone className="w-5 h-5 text-primary" />
                  <span>+1 (809) 123-4567</span>
                </a>
                <a href="mailto:info@tropunca.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                  <Mail className="w-5 h-5 text-primary" />
                  <span>info@tropunca.com</span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default About;
